import React, { useEffect, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { FiRefreshCcw } from "react-icons/fi";

/* ── Table cell styles (Transactionsreport1 reference) ── */
const TH = "border border-[#94A3B8] bg-[#EFF6FF] px-3 py-2.5 text-left text-[11px] font-medium text-[#0A2A4F] uppercase tracking-wider whitespace-nowrap select-none";
const TD = "border border-[#94A3B8] px-3 py-2.5 text-sm text-gray-700 align-middle";

const MerchantWallet = () => {

    const today = new Date().toISOString().split("T")[0];

    const username = useSelector((state) => state.auth.employeeDetails?.username);

    /* ── Balance ── */
    const { callApi: walletbalance } = useApi("/admin/cashin/wallet/balance", "GET");
    const [balance, setBalance] = useState(0);
    const [balanceLoading, setBalanceLoading] = useState(false);

    /* ── Ledger ── */
    const { callApi: ledgerreport } = useApi("/ledger/retailer", "GET");

    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);

    /* ── Pagination ── */
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(50);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [isLastPage, setIsLastPage] = useState(false);
    const [goToPage, setGoToPage] = useState("");

    /* ── Fetch balance ── */
    const fetchBalance = async () => {
        setBalanceLoading(true);
        try {
            const res = await walletbalance(null, `?userName=${username}`);
            if (res?.balance !== undefined) setBalance(res.balance);
        } catch (err) {
            console.error(err);
        } finally {
            setBalanceLoading(false);
        }
    };

    /* ── Build ledger query ── */
    const buildQuery = (targetPage, pageSize) => {
        let q = `?bid=${username}&page=${targetPage}&size=${pageSize}&scope=self`;
        if (fromDate) q += `&fromDate=${fromDate}`;
        if (toDate) q += `&toDate=${toDate}`;
        return q;
    };

    /* ── Fetch ledger ── */
    const fetchLedger = async (targetPage = 0, pageSize = size) => {
        setLoading(true);
        try {
            const res = await ledgerreport(null, buildQuery(targetPage, pageSize));
            if (res?.content) {
                setData(res.content);
                setPage(res.number ?? targetPage);
                setTotalPages(res.totalPages ?? 0);
                setTotalElements(res.totalElements ?? 0);
                setIsLastPage(res.last === true);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBalance();
        fetchLedger(0);
        // eslint-disable-next-line
    }, []);

    const handleRefresh = () => {
        fetchBalance();
        fetchLedger(0);
    };

    const handleFetchForPage = (targetPage) => {
        setPage(targetPage);
        fetchLedger(targetPage);
    };

    const handleGoToPage = () => {
        const p = parseInt(goToPage, 10);
        if (isNaN(p) || p < 1 || p > totalPages) return;
        handleFetchForPage(p - 1);
        setGoToPage("");
    };

    /* ── Download Excel ── */
    const downloadExcel = async () => {
        setDownloadLoading(true);
        try {
            const exportSize = totalElements > 0 ? totalElements : size;
            const res = await ledgerreport(null, buildQuery(0, exportSize));
            const rows = res?.content ?? [];
            if (!rows.length) return;

            const exportData = rows.map((row) => ({
                TxnID: row.txnId,
                Date: formatDateTime(row.txnDate),
                Narration: row.narration ?? "-",
                Amount: row.txnAmount,
                Type: row.txnType,
                Balance: row.balance,
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "WalletLedger");
            XLSX.writeFile(workbook, `Wallet_Ledger_${fromDate}_to_${toDate}.xlsx`);
        } catch (err) {
            console.error(err);
        } finally {
            setDownloadLoading(false);
        }
    };

    /* ── Helpers ── */
    const formatDateTime = (dateString) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit", hour12: false,
        });
    };

    /* ── Pagination pages helper ── */
    const getPaginationPages = (current, total) => {
        if (total <= 1) return [];
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
        const pages = [];
        const delta = 2;
        let prev = null;
        for (let i = 1; i <= total; i++) {
            if (i === 1 || i === total || (i >= current + 1 - delta && i <= current + 1 + delta)) {
                if (prev !== null && i - prev > 1) pages.push("...");
                pages.push(i);
                prev = i;
            }
        }
        return pages;
    };

    const paginationPages = getPaginationPages(page, totalPages);
    const rangeStart = totalElements === 0 ? 0 : page * size + 1;
    const rangeEnd = Math.min(page * size + data.length, totalElements);

    /* ════════════════ RENDER ════════════════ */
    return (
        <div className="bg-[#F0F4FA] min-h-screen p-4 md:p-6">
            <div className="mx-auto space-y-5 max-w-full">

                {/* ── Balance card ── */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 flex items-center justify-between gap-4">
                    <div>
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-1">Cash-In Wallet</p>
                        <h3 className={`text-3xl font-bold ${balanceLoading ? "text-gray-300 animate-pulse" : "text-green-600"}`}>
                            {balanceLoading ? "···" : `₹ ${Number(balance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
                        </h3>
                        <p className="text-xs text-gray-400 mt-1">Current Balance</p>
                    </div>
                    <button onClick={fetchBalance} disabled={balanceLoading}
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#EFF6FF] hover:bg-[#DBEAFE] text-[#0A2A4F] transition disabled:opacity-40 border border-[#BFDBFE]">
                        <FiRefreshCcw size={16} className={balanceLoading ? "animate-spin" : ""} />
                    </button>
                </div>

                {/* ── Ledger section ── */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">

                    {/* Header bar */}
                    <div className="bg-white border-b border-[#E5E7EB] px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-lg bg-[#0A2A4F] flex items-center justify-center text-white text-sm font-medium flex-shrink-0">≡</div>
                            <div>
                                <h2 className="text-[17px] font-semibold text-[#0A2A4F] leading-tight">Wallet Ledger</h2>
                                {totalElements > 0 && (
                                    <span className="text-[12px] text-gray-400">
                                        {rangeStart.toLocaleString("en-IN")}–{rangeEnd.toLocaleString("en-IN")} of {totalElements.toLocaleString("en-IN")} records
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {loading && <div className="w-4 h-4 border-2 border-[#0A2A4F] border-t-transparent rounded-full animate-spin" />}
                            <button onClick={downloadExcel} disabled={downloadLoading || !totalElements}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#16A34A] hover:bg-[#15803d] disabled:opacity-50 text-white font-medium text-[13px] transition">
                                ↓ {downloadLoading ? "Exporting…" : `Excel${totalElements > 0 ? ` (${totalElements.toLocaleString("en-IN")})` : ""}`}
                            </button>
                            <button onClick={handleRefresh} disabled={loading}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] disabled:opacity-50 text-white font-medium text-[13px] transition">
                                <FiRefreshCcw size={13} className={loading ? "animate-spin" : ""} /> Refresh
                            </button>
                        </div>
                    </div>

                    {/* Filter bar */}
                    <div className="bg-white border-b border-[#E5E7EB] px-4 py-3 flex flex-wrap gap-2 items-center">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">From</span>
                            <input type="date" max={today} value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 outline-none" />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">To</span>
                            <input type="date" min={fromDate} max={today} value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 outline-none" />
                        </div>
                        <button onClick={() => fetchLedger(0)} disabled={loading}
                            className="h-9 px-5 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] text-white font-medium text-[13px] transition disabled:opacity-50">
                            Apply
                        </button>
                    </div>

                    {/* Table */}
                    <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 340px)" }}>
                        <table className="border-collapse text-sm" style={{ minWidth: 700, width: "100%" }}>
                            <thead>
                                <tr>
                                    <th className={TH} style={{ width: 40 }}>#</th>
                                    <th className={TH} style={{ minWidth: 160 }}>Txn ID</th>
                                    <th className={TH} style={{ minWidth: 150 }}>Date</th>
                                    <th className={TH} style={{ minWidth: 220 }}>Narration</th>
                                    <th className={TH} style={{ minWidth: 110 }}>Amount</th>
                                    <th className={TH} style={{ minWidth: 90 }}>Type</th>
                                    <th className={TH} style={{ minWidth: 120 }}>Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    Array.from({ length: 8 }).map((_, i) => (
                                        <tr key={i} className="animate-pulse">
                                            {Array.from({ length: 7 }).map((__, j) => (
                                                <td key={j} className={TD}>
                                                    <div className="h-3.5 bg-gray-200 rounded-full" style={{ width: j === 0 ? "60%" : "80%" }} />
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : data.length > 0 ? (
                                    data.map((row, i) => (
                                        <tr key={row.txnId ?? i} className={i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                                            <td className={TD + " text-center text-xs text-gray-400"}>{page * size + i + 1}</td>
                                            <td className={TD + " font-mono text-xs"}>{row.txnId ?? "-"}</td>
                                            <td className={TD + " text-xs"}>{formatDateTime(row.txnDate)}</td>
                                            <td className={TD}>
                                                <div className="break-words whitespace-normal text-xs leading-snug">{row.narration ?? "-"}</div>
                                            </td>
                                            <td className={TD + " font-semibold"}>
                                                ₹{Number(row.txnAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className={TD}>
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-medium border ${
                                                    row.txnType === "CREDIT"
                                                        ? "bg-white border-green-200 text-green-600"
                                                        : "bg-[#FEE2E2] border-red-200 text-red-600"
                                                }`}>
                                                    {row.txnType}
                                                </span>
                                            </td>
                                            <td className={TD + " font-medium"}>
                                                ₹{Number(row.balance ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7} className={TD + " py-16 text-center border-0"}>
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center text-3xl shadow-inner">📭</div>
                                                <p className="text-gray-500 text-sm font-medium">No ledger records found</p>
                                                <p className="text-gray-400 text-xs">Try adjusting the date range</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Pagination bar ── */}
                    {!loading && totalPages > 0 && (
                        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-[#E5E7EB] bg-white overflow-x-auto flex-nowrap">

                            <div className="flex items-center gap-1.5 flex-nowrap flex-shrink-0">
                                <button onClick={() => handleFetchForPage(page - 1)} disabled={page === 0}
                                    className="h-8 px-3.5 rounded-lg border border-gray-200 bg-white text-[#0A2A4F] text-[13px] font-medium hover:bg-[#EFF6FF] disabled:opacity-30 disabled:cursor-not-allowed transition flex-shrink-0">
                                    ‹ Prev
                                </button>
                                {paginationPages.map((p, i) =>
                                    p === "..." ? (
                                        <span key={`e${i}`} className="h-8 w-8 flex items-center justify-center text-gray-400 text-sm select-none flex-shrink-0">…</span>
                                    ) : (
                                        <button key={p} onClick={() => handleFetchForPage(p - 1)}
                                            className={`h-8 min-w-[34px] px-2 rounded-lg text-[13px] font-medium transition border flex-shrink-0 ${
                                                page === p - 1
                                                    ? "bg-[#0A2A4F] text-white border-[#0A2A4F]"
                                                    : "bg-white text-gray-500 border-gray-200 hover:bg-[#EFF6FF] hover:text-[#0A2A4F]"
                                            }`}>
                                            {p}
                                        </button>
                                    )
                                )}
                                <button onClick={() => handleFetchForPage(page + 1)} disabled={isLastPage}
                                    className="h-8 px-3.5 rounded-lg border border-gray-200 bg-white text-[#0A2A4F] text-[13px] font-medium hover:bg-[#EFF6FF] disabled:opacity-30 disabled:cursor-not-allowed transition flex-shrink-0">
                                    Next ›
                                </button>
                            </div>

                            <div className="flex items-center gap-2 flex-nowrap flex-shrink-0">
                                <span className="text-[12px] text-gray-400 whitespace-nowrap">Rows</span>
                                {[50, 100, 200, 500].map((s) => (
                                    <button key={s} onClick={() => { setSize(s); setPage(0); fetchLedger(0, s); }}
                                        className={`h-8 min-w-[44px] px-3 rounded-full text-[13px] font-medium transition border whitespace-nowrap flex-shrink-0 ${
                                            size === s ? "bg-[#0A2A4F] text-white border-[#0A2A4F]" : "bg-white text-gray-500 border-gray-200 hover:bg-[#EFF6FF] hover:text-[#0A2A4F]"
                                        }`}>
                                        {s}
                                    </button>
                                ))}
                                <div className="w-px h-6 bg-gray-200 flex-shrink-0 mx-1" />
                                <span className="text-[12px] text-gray-400 whitespace-nowrap">Go to</span>
                                <input type="number" min={1} max={totalPages} placeholder="Page" value={goToPage}
                                    onChange={(e) => setGoToPage(e.target.value)}
                                    className="h-8 w-20 px-2.5 border border-gray-200 rounded-lg bg-white text-[13px] text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 outline-none flex-shrink-0" />
                                <button onClick={handleGoToPage}
                                    className="h-8 px-4 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] text-white font-medium text-[13px] transition flex-shrink-0">
                                    Go
                                </button>
                            </div>

                        </div>
                    )}

                </div>

            </div>
        </div>
    );
};

export default MerchantWallet;
